import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {SnackbarService} from '../../../../infrastructure/service/snackbar.service';

@Component({
    selector: 'app-avatar',
    templateUrl: './avatar.component.html',
    styleUrls: ['./avatar.component.scss'],
    standalone: false
})
export class AvatarComponent implements OnInit {

  @ViewChild('cropPreviewCanvas')
  cropPreviewCanvas?: ElementRef<HTMLCanvasElement>;

  @Input()
  entityWithAvatar: any;

  @Input()
  size = 'sm';

  @Input()
  tourIdPrefix = '';

  @Output()
  avatarUpdated = new EventEmitter<string | undefined>();

  imageSrc!: string;
  openCropper = false;
  sourceImageDataUrl?: string;
  sourceImageElement?: HTMLImageElement;
  sourceMimeType = 'image/jpeg';
  zoom = 1;
  offsetX = 0;
  offsetY = 0;
  readonly maxOutputSize = 1024;

  private dragActive = false;
  private dragStartX = 0;
  private dragStartY = 0;

  constructor(private snackbarService: SnackbarService) { }

  ngOnInit(): void {
  }

  uploadAvatar(e: any): void {
    if (!this.entityWithAvatar) {
      return;
    }

    const file = e.dataTransfer ? e.dataTransfer.files[0] : e.target.files[0];
    if (!file) {
      return;
    }

    const supportedFormats = ['image/jpeg', 'image/png', 'image/webp'];
    if (!supportedFormats.includes(file.type)) {
      this.snackbarService.error('Formato inválido. Use JPG, PNG ou WEBP.');
      return;
    }

    this.sourceMimeType = file.type;

    const reader = new FileReader();
    reader.onload = async () => {
      const result = reader.result as string;
      this.sourceImageDataUrl = result;
      this.sourceImageElement = await this.loadImage(result);

      this.zoom = 1;
      this.offsetX = 0;
      this.offsetY = 0;
      this.openCropper = true;

      setTimeout(() => this.renderCropPreview(), 10);
    };
    reader.readAsDataURL(file);
  }

  deleteAvatar(): void {
    this.avatarUpdated.emit(undefined);
  }

  getAvatarClassName(size: string): string {
    return `avatar avatar-${size} rounded-circle`;
  }

  onZoomChange(event: any): void {
    this.zoom = Number(event.target.value || 1);
    this.renderCropPreview();
  }

  onStartDrag(event: MouseEvent): void {
    this.dragActive = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
  }

  onDrag(event: MouseEvent): void {
    if (!this.dragActive) {
      return;
    }

    const dx = event.clientX - this.dragStartX;
    const dy = event.clientY - this.dragStartY;

    this.offsetX += dx;
    this.offsetY += dy;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.renderCropPreview();
  }

  onEndDrag(): void {
    this.dragActive = false;
  }

  cancelCrop(): void {
    this.openCropper = false;
  }

  applyCrop(): void {
    const result = this.exportCroppedImage();
    if (!result) {
      this.snackbarService.error('Não foi possível processar a imagem.');
      return;
    }

    this.imageSrc = result;
    this.avatarUpdated.emit(this.imageSrc);
    this.openCropper = false;
  }

  private renderCropPreview(): void {
    if (!this.cropPreviewCanvas?.nativeElement) {
      return;
    }

    this.drawToCanvas(this.cropPreviewCanvas.nativeElement, 320);
  }

  private exportCroppedImage(): string | undefined {
    const outputSize = Math.min(this.maxOutputSize, this.sourceImageElement?.width || this.maxOutputSize, this.sourceImageElement?.height || this.maxOutputSize);
    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;

    this.drawToCanvas(canvas, outputSize);

    if (this.sourceMimeType === 'image/png') {
      return canvas.toDataURL('image/png');
    }

    return canvas.toDataURL('image/jpeg', 0.88);
  }

  private drawToCanvas(canvas: HTMLCanvasElement, size: number): void {
    if (!this.sourceImageElement) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const image = this.sourceImageElement;

    ctx.clearRect(0, 0, size, size);

    const baseScale = Math.max(size / image.width, size / image.height);
    const scale = baseScale * this.zoom;
    const drawWidth = image.width * scale;
    const drawHeight = image.height * scale;

    const x = (size - drawWidth) / 2 + this.offsetX;
    const y = (size - drawHeight) / 2 + this.offsetY;

    ctx.drawImage(image, x, y, drawWidth, drawHeight);
  }

  private loadImage(imageURL: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = imageURL;
    });
  }
}
